<i18n>
{
	"en": {
		"arrival": "arrival",
		"consignee": "consignee",
		"consignor": "consignor",
		"dateComplete": "completed",
		"datePlanned": "planned",
		"departure": "departure",
		"editInformation": "Edit Information",
		"freeText": "Free Text",
		"freightLines": "Freight Lines",
		"from": "From",
		"loadingInformation": "Cargo Information",
		"log": "Log",
		"noFreeText": "There are no free texts yet.",
		"noFreightLines": "There are no freight lines yet.",
		"noLoadingInformation": "There is no loading information yet.",
		"noPriceLines": "There are no pricelines yet.",
		"order": "Order",
		"packages": "packages",
		"priceLines": "Price Lines",
		"to": "To",
		"viewInMap": "View in Map",
		"waybill": "Waybill",
		"waybillNumber": "Waybill no.",
		"accepted": "Accepted",
		"cancelled": "Cancelled",
		"incomplete": "Incomplete",
		"invoiced": "Invoiced",
		"notSettled": "Not Settled",
		"recorded": "Recorded",
		"settled": "Settled"
	},
	"no": {
		"arrival": "Ankomst",
		"consignee": "mottaker",
		"consignor": "avsender",
		"dateComplete": "bekreftet",
		"datePlanned": "planlagt",
		"departure": "Avgang",
		"editInformation": "Rediger opplysninger",
		"freeText": "Fritekster",
		"freightLines": "Fraktlinjer",
		"from": "Fra",
		"loadingInformation": "Lasteopplysninger",
		"log": "Logg",
		"noFreeText": "Det er ingen fritekster ennå.",
		"noFreightLines": "Det er ingen fraktlinjer ennå.",
		"noLoadingInformation": "Det er ingen lasteopplysninger ennå.",
		"noPriceLines": "Det er ingen prislinjer ennå.",
		"order": "Oppdrag",
		"packages": "kolli",
		"priceLines": "Prislinjer",
		"to": "Til",
		"viewInMap": "Se i kart",
		"waybill": "Fraktbrev",
		"waybillNumber": "Fraktbrevnr",
		"accepted": "Akseptert",
		"cancelled": "Annullert",
		"incomplete": "Ufullstendig",
		"invoiced": "Oppgjort",
		"notSettled": "Ikke klarert",
		"recorded": "Bokført",
		"settled": "Klarert"
	}
}
</i18n>
<template>
	<t-vertical-panel v-if="orderDetails">
		<div slot="header" class="order-details-header">
			<t-panel-header :icon="getIcon" :color="getStatusColor" :text="`${$t('order')} ${orderDetails.orderNumber}`"/>
			<div class="order-details-header-sub-label">
				{{$t('waybillNumber')}} {{orderDetails.waybillNumber}}
			</div>
			<t-icon-button
				style="margin-right: 10px;"
				appearance="minimal"
				type="bp"
				icon="cross"
				iconSize="md"
				@click="hideDetails"
			/>
		</div>
		<div slot="sub-header" ref="subHeader" class="order-details-sub-header">
			<div v-if="!smallSubHeader" class="buttons-with-text">
				<t-button
					:value="$t('editInformation')"
					appearance="minimal"
					type="bp"
					iconBefore="annotation"
					@click="setEditOrder"
				/>
				<t-button
					:value="$t('viewInMap')"
					appearance="minimal"
					type="bp"
					iconBefore="globe"
				/>
				<div/>
				<t-button
					:value="$t('waybill')"
					appearance="minimal"
					type="timpex"
					iconBefore="waybill"
					@click="getWaybill(orderDetails.id)"
				/>
				<t-button
					:value="$t('log')"
					type="bp"
					iconBefore="history"
					appearance="minimal"
					:disabled="!orderDetails.events"
					@click="showOrderLogDialog = true"
				/>
			</div>
			<div v-if="smallSubHeader" class="buttons-no-text">
				<t-icon-button
					:value="$t('editInformation')"
					appearance="minimal"
					type="bp"
					icon="annotation"
					@click="setEditOrder"
				/>
				<t-icon-button
					:value="$t('viewInMap')"
					appearance="minimal"
					type="bp"
					icon="globe"
				/>
				<div/>
				<t-button
					:value="$t('waybill')"
					appearance="minimal"
					type="timpex"
					iconBefore="waybill"
					@click="getWaybill(orderDetails.id)"
				/>
				<t-button
					:value="$t('log')"
					type="bp"
					iconBefore="history"
					appearance="minimal"
					:disabled="!orderDetails.events"
					@click="showOrderLogDialog = true"
				/>
			</div>
		</div>
		<div v-show="!orderDetailsLoading" class="details-content">
			<div class="status">
				<t-badge :color="economicalStatusColor" bold>{{$t(economicalStatus)}}</t-badge>
				<t-badge :color="operationalStatusColor" bold>{{operationalStatus}}</t-badge>
			</div>
			<div class="details">
				<t-pane
					class="details-info"
					border-radius="5px"
					background="default"
					border="default"
				>
					<t-destinations-large>
						<template v-slot:from>
							<t-details-item
								:label="$t('from')"
								:primary="getProperty(orderDetails.despatchParty, 'name')"
								:secondary="addressLine(orderDetails.despatchParty)"
							/>

							<t-details-item
								:label="$t('consignor')"
								:primary="getProperty(orderDetails.consignor, 'name')"
							/>

							<t-details-item
								:primary="dateFormatter(orderDetails.atd, orderDetails.etd)"
								:primaryClass="etdClass"
							>
								<template v-slot:label>
									{{$t('departure')}} <span class="lowercase">({{datePlannedOrComplete(orderDetails.ata)}})</span>
								</template>
							</t-details-item>
						</template>
						<template v-slot:to>
							<t-details-item
								:label="$t('to')"
								:primary="getProperty(orderDetails.deliveryParty, 'name')"
								:secondary="addressLine(orderDetails.deliveryParty)"
							/>
							<t-details-item :label="$t('consignee')" :primary="getProperty(orderDetails.consignee, 'name')"/>
							<t-details-item
								:primaryClass="etaClass"
								:primary="dateFormatter(orderDetails.ata, orderDetails.eta)"
							>
								<template v-slot:label>
									{{$t('arrival')}} <span class="lowercase">({{datePlannedOrComplete(orderDetails.ata)}})</span>
								</template>
							</t-details-item>
						</template>
					</t-destinations-large>
				</t-pane>

				<t-pane
					v-if="orderDetails.customer"
					class="details-customer"
					border-radius="5px"
					background="default"
					border="default"
				>
					{{ getProperty(orderDetails.customer, 'name')}}
				</t-pane>

				<t-pane
					v-if="hasContactInfo"
					class="details-contacts"
					border-radius="5px"
					background="default"
					border="default"
				>
					<t-contact-info
						v-if="orderDetails.customer"
						:name="getProperty(orderDetails.customer,'contactName')"
						:phone="getProperty(orderDetails.customer,'contactPhone')"
						:company="getProperty(orderDetails.customer,'name')"
					/>
					<t-contact-info
						v-if="orderDetails.consignor"
						:name="getProperty(orderDetails.consignor,'contactName')"
						:phone="getProperty(orderDetails.consignor,'contactPhone')"
						:company="getProperty(orderDetails.consignor,'name')"
					/>
					<t-contact-info
						v-if="orderDetails.consignee"
						:name="getProperty(orderDetails.consignee,'contactName')"
						:phone="getProperty(orderDetails.consignee,'contactPhone')"
						:company="getProperty(orderDetails.consignee,'name')"
					/>
					<t-contact-info
						v-if="orderDetails.despatchParty"
						:name="getProperty(orderDetails.despatchParty,'contactName')"
						:phone="getProperty(orderDetails.despatchParty,'contactPhone')"
						:company="getProperty(orderDetails.despatchParty,'name')"
					/>
					<t-contact-info
						v-if="orderDetails.deliveryParty"
						:name="getProperty(orderDetails.deliveryParty,'contactName')"
						:phone="getProperty(orderDetails.deliveryParty,'contactPhone')"
						:company="getProperty(orderDetails.deliveryParty,'name')"
					/>
				</t-pane>
			</div>

			<t-tab-view
				:tabs="tabs"
				:orderDetails="orderDetails"
			/>
		</div>
		<t-overlay
			:isShown="orderDetailsLoading"
			absolute
			loader
		/>
		<Log
			:isShown="showOrderLogDialog"
			:order="orderDetails"
			@close="showOrderLogDialog = false"
		/>
	</t-vertical-panel>
</template>

<script>
import {
	displayDateFormatter,dateExists, goodsTypesIcons, statuses, eventBus
} from '@/core';
import { mapDynamicActions } from '@/core/mapDynamic';
import FreightLines from './OrderDetails/FreightLines.vue';
import PriceLines from './OrderDetails/PriceLines.vue';
import LoadingInformation from './OrderDetails/LoadingInformation.vue';
import FreeText from './OrderDetails/FreeText.vue';
import Log from './OrderDetails/Log';
import TContactInfo from '@/components/evergreen/timpex/layers/TContactInfo';
export default {
	name: 'OrderDetails',
	components: { TContactInfo, Log },
	props: { orderDetails: Object, orderDetailsLoading: Boolean },
	data() {
		return {
			showOrderLogDialog: false,
			subHeaderWidth: null
		};
	},
	computed: {
		tabs() {
			return [
				{ component: FreightLines, label: this.$t('freightLines') },
				{ component: PriceLines, label: this.$t('priceLines') },
				{ component: LoadingInformation, label: this.$t('loadingInformation') },
				{ component: FreeText, label: this.$t('freeText') }
			];
		},
		etdClass() {
			return dateExists(this.orderDetails.atd) ? '' : 'italics';
		},
		etaClass() {
			return dateExists(this.orderDetails.ata) ? '' : 'italics';
		},
		getIcon() {
			return goodsTypesIcons(this.getProperty(this.orderDetails.goodsType, 'id'));
		},
		getStatusColor() {
			return statuses.color(this.orderDetails.status);
		},
		hasContactInfo() {
			return this.getProperty(this.orderDetails.customer,'contactPhone') ||
				this.getProperty(this.orderDetails.consignor,'contactPhone') ||
				this.getProperty(this.orderDetails.consignee,'contactPhone') ||
				this.getProperty(this.orderDetails.despatchParty,'contactPhone') ||
				this.getProperty(this.orderDetails.deliveryParty,'contactPhone');
		},
		searchId() {
			return this.$route.params.searchId || -1;
		},
		computedStoreNamespace() {
			return `orderSearch/${this.searchId}`;
		},
		smallSubHeader() {
			return this.subHeaderWidth < 600;
		},
		operationalStatus() {
			return statuses.operational(this.orderDetails.status);
		},
		operationalStatusColor() {
			return statuses.color(this.orderDetails.status);
		},
		economicalStatus() {
			return statuses.economical(this.orderDetails.economicalStatus);
		},
		economicalStatusColor() {
			return statuses.color(this.orderDetails.economicalStatus);
		}
	},
	async mounted() {
		await this.$nextTick();
		let { subHeader } = this.$refs;
		if (subHeader) {
			this.subHeaderObserver = new ResizeObserver((entries) => {
				let { width } = subHeader.getBoundingClientRect();
				console.log({ observation: entries, width });
				this.subHeaderWidth = width;
			});
			this.subHeaderObserver.observe(subHeader);
		}
	},
	beforeDestroy() {
		if (this.subHeaderObserver) this.subHeaderObserver.disconnect();
	},
	methods: {
		...mapDynamicActions('computedStoreNamespace', ['getWaybill']),
		hideDetails() {
			this.$emit('hide');
		},
		addressLine(party) {
			if (!party) return '';
			return `${party.postalCode} ${party.postalPlace}`;
		},
		getProperty(object, propKey) {
			if (!object) return '';
			return object[propKey];
		},
		dateFormatter(actual, estimated) {
			let dateObject = dateExists(actual) ? actual : estimated;
			if (!dateObject || !dateObject.date) return '';
			return displayDateFormatter(dateObject.date);
		},
		datePlannedOrComplete(actual) {
			return dateExists(actual) ? this.$t('dateComplete') : this.$t('datePlanned');
		},
		setEditOrder() {
			eventBus.$emit('setEditOrder', this.orderDetails.id);
		}
	}
};
</script>

<style lang="scss" scoped>
.order-details-header {
	height: 100%;
	display:flex;
	align-items: center;
	padding-left: 15px;
	.order-details-header-sub-label {
		flex: 1;
		color: #66788a;
		font-size: 14px;
	}
}

.order-details-sub-header {
	height: 100%;
	.buttons-with-text {
		display:grid;
		padding: 0px 10px;
		height: 100%;
		align-items: center;
		grid-template-columns: repeat(2, minmax(100px, max-content)) auto repeat(2, minmax(100px, max-content));
		grid-column-gap: 10px;
	}
	.buttons-no-text {
		display:grid;
		padding: 0px 10px;
		height: 100%;
		align-items: center;
		grid-template-columns: repeat(3, minmax(min-content, max-content)) auto repeat(3, minmax(min-content, max-content));
		grid-column-gap: 10px;
	}
}

.lowercase {
	text-transform: lowercase;
}

.details-content {
	min-height:100%;
	display: grid;
	background: white;
	padding: 20px;
	row-gap: 20px;
	grid-template-rows: repeat(2, minmax(min-content, max-content)) minmax(min-content, max-content);
}
.status {
	display: grid;
	column-gap: 10px;
	grid-template-columns: repeat(2, max-content);
}

.details {
	display: grid;
	row-gap: 10px;
	grid-template-rows: repeat(2, minmax(min-content, max-content));
}

.details-info {
	padding: 10px 20px;
}

.details-customer, .details-contacts {
	padding: 15px 20px;
}
</style>
